import { FC } from 'react'
import cx from 'classnames'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useAtom } from 'jotai'

import { PrimaryButtonLink, TanButton } from 'ui'

import { CustomerAttribute } from '../../services/customer/loadAttributes'
import { HomeOffer } from '../../services/offer/status'
import { useTracking } from '../../services/context/TrackingContext'

import { getApprovedRedirectPathAtom } from '../../utils/atoms'

const ActionButton: FC<{
  className?: string
  customerApprovalStatus?: CustomerAttribute['approval']['status']
  customerOfferStatus?: HomeOffer['status']
  existingOffer: boolean
  listingId: number
  offMarket: boolean
  propertyStatus?: string
}> = ({
  className,
  customerApprovalStatus,
  customerOfferStatus,
  listingId,
  existingOffer,
  offMarket,
  propertyStatus,
}) => {
  const { t } = useTranslation(['common', 'homes'])
  const router = useRouter()
  const { track } = useTracking()
  const [, updateGetApprovedRedirectPathAtom] = useAtom(
    getApprovedRedirectPathAtom
  )

  if (propertyStatus === 'reserved' || propertyStatus === 'acre-bought') {
    return (
      <Link href="/book" passHref>
        <PrimaryButtonLink
          size="lg"
          className={cx(className, 'block w-full text-center')}
          onClick={() => {
            return null
          }}
        >
          {t('navbar.book-a-call')}
        </PrimaryButtonLink>
      </Link>
    )
  }

  if (existingOffer) {
    // @TODO differentiate between user having the offer or someone else having it?
    return (
      <TanButton
        size="lg"
        className={cx(className, 'w-full')}
        disabled
        onClick={() => null}
      >
        {t('offerPendingBtn', { ns: 'homes' })}
      </TanButton>
    )
  }

  if (customerOfferStatus === 'open') {
    return (
      <p
        className={cx(
          className,
          'rounded-sm border border-green-1 bg-grey-2 p-20 text-center'
        )}
      >
        {t('anotherOfferPending', { ns: 'homes' })}
      </p>
    )
  }

  if (offMarket) {
    return (
      <p
        data-testid="off-market"
        className={cx(
          className,
          'rounded-sm border border-green-1 bg-grey-2 p-20 text-center'
        )}
      >
        {t('ui.home-is-off-market')}
      </p>
    )
  }

  if (customerApprovalStatus === 'approved') {
    return (
      <Link href={`/make-offer?lid=${encodeURIComponent(listingId)}`} passHref>
        <PrimaryButtonLink
          size="lg"
          className={cx(className, 'block w-full text-center')}
          onClick={() => {
            return null
          }}
        >
          {t('makeOfferBtn', { ns: 'homes' })}
        </PrimaryButtonLink>
      </Link>
    )
  }

  if (['pending', 'under review'].includes(customerApprovalStatus || '')) {
    return (
      <TanButton
        size="lg"
        className={cx(className, 'w-full')}
        disabled
        onClick={() => null}
      >
        {t('bidPendingBtn', { ns: 'homes' })}
      </TanButton>
    )
  }

  return (
    <Link passHref href={`/get-approved?listingId=${listingId}`}>
      <PrimaryButtonLink
        className={cx(className, 'block w-full text-center')}
        size="lg"
        onClick={() => {
          updateGetApprovedRedirectPathAtom(router.asPath)
          track({
            event: 'Approval Flow Opened',
            properties: {
              link_type: 'Button',
              link_location: `Listing ${listingId}`,
              link_position: 'Right',
              link_priority: 1,
            },
          })
        }}
      >
        {t('bidApproveBtn', { ns: 'homes' })}
      </PrimaryButtonLink>
    </Link>
  )
}

export default ActionButton
